import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Loader2, Plus, Camera, Trash2, Send, Wifi, WifiOff,
  AlertTriangle, ImageIcon, Check, X, ZoomIn, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import offlineDb from '../../services/offlineDb';
import {
  saveObservationLocally,
  getLocalObservations,
  queuePhotoUpload,
  getQueuedPhotos,
  syncObservations,
  removeLocalObservation,
  generateLocalId,
} from '../../services/observationsOfflineService';
import { getCountPackage } from '../../services/countOfflineService';

const MAX_PHOTOS = 3;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'damage', label: 'Damage' },
  { value: 'expiry', label: 'Expiry' },
  { value: 'discrepancy', label: 'Discrepancy' },
  { value: 'hygiene', label: 'Hygiene' },
  { value: 'security', label: 'Security' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES = [
  { value: 'info', label: 'Info', color: 'bg-slate-100 text-slate-700' },
  { value: 'low', label: 'Low', color: 'bg-blue-50 text-blue-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-50 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-orange-50 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-50 text-red-700' },
];

export default function CountObservationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionId = id ? parseInt(id, 10) : null;
  const [sessionInfo, setSessionInfo] = useState(null);
  const [observations, setObservations] = useState([]);
  const [serverObs, setServerObs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [quickCaptureMode, setQuickCaptureMode] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Load session info from IndexedDB
  useEffect(() => {
    async function loadSession() {
      if (!sessionId) { setLoading(false); return; }
      const pkg = await getCountPackage(sessionId);
      if (pkg) {
        setSessionInfo({
          site_name: pkg.site_name,
          session_reference: pkg.session_reference,
        });
      }
      setLoading(false);
    }
    loadSession();
  }, [sessionId]);

  // Load observations — local + server
  const loadObservations = useCallback(async () => {
    if (!sessionId) return;
    const local = await getLocalObservations(sessionId);
    setObservations(local);
    if (isOnline) {
      try {
        const res = await api.get(`/count/sessions/${sessionId}/observations`);
        setServerObs(res.data.items || []);
      } catch {
        // Offline or error — just use local
      }
    }
  }, [sessionId, isOnline]);

  useEffect(() => { loadObservations(); }, [loadObservations]);

  // Create observation
  const handleCreate = async (formData) => {
    const saved = await saveObservationLocally(sessionId, formData);
    setShowForm(false);
    setQuickCaptureMode(false);
    toast.success('Observation saved locally');

    if (isOnline) {
      try {
        const res = await api.post(`/count/sessions/${sessionId}/observations`, {
          ...formData,
          local_id: saved.local_id,
        });
        const serverId = res.data?.observation?.id;
        const updatedObs = { ...saved, synced: true, server_id: serverId || null };
        await offlineDb.put(offlineDb.STORES.OBSERVATIONS, updatedObs);

        if (serverId) {
          const photos = await getQueuedPhotos(saved.local_id);
          for (const photo of photos) {
            const { uploadQueuedPhoto } = await import('../../services/observationsOfflineService');
            await uploadQueuedPhoto(photo, serverId);
          }
        }
      } catch {
        // Will sync later
      }
    }
    loadObservations();
  };

  // Delete observation
  const handleDelete = async (obs) => {
    const localId = obs.local_id;
    const serverId = obs.server_id || obs.id;
    
    // Delete from server if synced
    if ((obs._fromServer || obs.synced) && isOnline && serverId) {
      try {
        await api.delete(`/count/observations/${serverId}`);
      } catch {
        // May already be deleted
      }
    }
    // Delete from IndexedDB if local
    if (localId) {
      await removeLocalObservation(localId);
    }
    toast.info('Observation removed');
    loadObservations();
  };

  // Update observation (inline edit)
  const handleUpdate = async (obs, updates) => {
    if (obs._fromServer || obs.synced) {
      // Update on server via API
      const obsId = obs.server_id || obs.id;
      if (isOnline && obsId) {
        try {
          await api.patch(`/count/observations/${obsId}`, updates);
          toast.success('Observation updated');
          loadObservations();
        } catch (err) {
          toast.error('Failed to update: ' + (err.response?.data?.detail || err.message));
        }
      } else {
        toast.error('Cannot update server observation while offline');
      }
    } else {
      // Update in IndexedDB
      const updated = { ...obs, ...updates, updated_at: new Date().toISOString() };
      await offlineDb.put(offlineDb.STORES.OBSERVATIONS, updated);
      toast.success('Observation updated locally');
      loadObservations();
    }
  };

  // Sync all
  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncObservations(sessionId);
      const parts = [];
      if (result.created > 0) parts.push(`${result.created} observations`);
      if (result.photos_uploaded > 0) parts.push(`${result.photos_uploaded} photos`);
      toast.success(parts.length > 0 ? `Synced: ${parts.join(', ')}` : 'All up to date');
      loadObservations();
    } catch (e) {
      toast.error(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="p-4 max-w-2xl mx-auto text-center py-16" data-testid="count-observations-page">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        <p className="text-slate-500">No session specified</p>
        <p className="text-sm text-slate-400 mt-1">Open a session and use the Observations tab.</p>
      </div>
    );
  }

  // Merge local + server observations, dedup by local_id
  const allObs = [...observations];
  const localIds = new Set(observations.map(o => o.local_id));
  for (const so of serverObs) {
    if (!localIds.has(so.local_id)) {
      allObs.push({ ...so, synced: true, _fromServer: true });
    }
  }
  allObs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const unsyncedCount = observations.filter(o => !o.synced).length;

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="count-observations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/count/sessions/${sessionId}`)} data-testid="observations-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="observations-title">Observations</h1>
            <p className="text-sm text-slate-500">{sessionInfo?.site_name} &middot; {sessionInfo?.session_reference}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unsyncedCount > 0 && isOnline && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleSync}
              disabled={syncing}
              data-testid="observations-sync-btn"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Sync ({unsyncedCount})
            </Button>
          )}
          <div className={`text-xs ${isOnline ? 'text-green-600' : 'text-amber-600'}`}>
            {isOnline ? <Wifi className="w-4 h-4 inline" /> : <WifiOff className="w-4 h-4 inline" />}
          </div>
        </div>
      </div>

      {/* Action buttons — Add Observation + Quick Photo */}
      <div className="flex gap-2 mb-4">
        <Button
          className="flex-1 h-12"
          onClick={() => { setShowForm(true); setQuickCaptureMode(false); }}
          data-testid="observations-add-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Observation
        </Button>
        <Button
          variant="outline"
          className="h-12 px-4"
          onClick={() => { setShowForm(true); setQuickCaptureMode(true); }}
          data-testid="observations-quick-photo-btn"
        >
          <Camera className="w-5 h-5 mr-1.5" />
          Quick Photo
        </Button>
      </div>

      {/* Observation form */}
      {showForm && (
        <ObservationForm
          onSubmit={handleCreate}
          onCancel={() => { setShowForm(false); setQuickCaptureMode(false); }}
          sessionId={sessionId}
          quickCapture={quickCaptureMode}
        />
      )}

      {/* Observations list */}
      {allObs.length === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="observations-empty">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No observations yet</p>
          <p className="text-xs mt-1">Tap "Add Observation" to log notes and photos.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="observations-list">
          {allObs.map(obs => (
            <ObservationCard
              key={obs.local_id || obs.id}
              obs={obs}
              onDelete={() => handleDelete(obs)}
              onPhotoTap={setLightboxSrc}
              onUpdate={handleUpdate}
              sessionId={sessionId}
              isOnline={isOnline}
              onReload={loadObservations}
            />
          ))}
        </div>
      )}

      {/* Photo lightbox */}
      {lightboxSrc && (
        <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
}

/* ─── Photo Lightbox ─── */
function PhotoLightbox({ src, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
        data-testid="lightbox-close-btn"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={src}
        alt="Full size"
        className="max-w-full max-h-[85vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/* ─── Observation Form (with multi-photo + quick-capture) ─── */
function ObservationForm({ onSubmit, onCancel, sessionId, quickCapture }) {
  const [form, setForm] = useState({
    note_text: '',
    category: 'general',
    severity: 'info',
    product_name_snapshot: '',
    location_name_snapshot: '',
  });
  const [photos, setPhotos] = useState([]); // Array of { file, preview, caption }
  const fileInputRef = useRef(null);
  const hasAutoOpened = useRef(false);

  // Quick-capture mode: auto-open camera on mount
  useEffect(() => {
    if (quickCapture && !hasAutoOpened.current && fileInputRef.current) {
      hasAutoOpened.current = true;
      // Small delay to let DOM render
      setTimeout(() => fileInputRef.current?.click(), 150);
    }
  }, [quickCapture]);

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.info(`Maximum ${MAX_PHOTOS} photos per observation`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotos(prev => [...prev, { file, preview: ev.target.result, caption: '' }]);
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const updateCaption = (index, caption) => {
    setPhotos(prev => prev.map((p, i) => i === index ? { ...p, caption } : p));
  };

  const handleSubmit = async () => {
    if (!form.note_text.trim() && photos.length === 0) return;

    const localId = generateLocalId();
    const obsData = {
      ...form,
      note_text: form.note_text.trim() || (photos.length > 0 ? 'Photo observation' : ''),
      local_id: localId,
    };

    // Queue all photos
    for (const photo of photos) {
      await queuePhotoUpload(localId, photo.file, photo.caption || null);
    }

    onSubmit(obsData);
  };

  const canSubmit = form.note_text.trim() || photos.length > 0;

  return (
    <Card className="p-4 mb-4" data-testid="observation-form">
      <div className="space-y-3">
        {/* Note text */}
        <textarea
          value={form.note_text}
          onChange={(e) => setForm(prev => ({ ...prev, note_text: e.target.value }))}
          placeholder={quickCapture ? 'Add a note (optional)' : 'What did you observe?'}
          rows={quickCapture ? 2 : 3}
          className="w-full text-sm border border-slate-200 rounded-lg p-3 resize-none focus:ring-1 focus:ring-slate-300"
          autoFocus={!quickCapture}
          data-testid="observation-note-input"
        />

        {/* Category + Severity row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
              className="w-full h-10 px-2 text-sm border border-slate-200 rounded-lg bg-white"
              data-testid="observation-category-select"
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Severity</label>
            <select
              value={form.severity}
              onChange={(e) => setForm(prev => ({ ...prev, severity: e.target.value }))}
              className="w-full h-10 px-2 text-sm border border-slate-200 rounded-lg bg-white"
              data-testid="observation-severity-select"
            >
              {SEVERITIES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Optional product/location — collapsed in quick-capture mode */}
        {!quickCapture && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Product (optional)</label>
              <Input
                value={form.product_name_snapshot}
                onChange={(e) => setForm(prev => ({ ...prev, product_name_snapshot: e.target.value }))}
                placeholder="Product name"
                className="h-10"
                data-testid="observation-product-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Location (optional)</label>
              <Input
                value={form.location_name_snapshot}
                onChange={(e) => setForm(prev => ({ ...prev, location_name_snapshot: e.target.value }))}
                placeholder="Location"
                className="h-10"
                data-testid="observation-location-input"
              />
            </div>
          </div>
        )}

        {/* Photo capture — multi-photo gallery */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoCapture}
            className="hidden"
            data-testid="observation-photo-file-input"
          />

          {/* Photo previews */}
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-2" data-testid="observation-photo-gallery">
              {photos.map((photo, idx) => (
                <div key={idx} className="relative" data-testid={`observation-photo-${idx}`}>
                  <img
                    src={photo.preview}
                    alt={`Preview ${idx + 1}`}
                    className="w-full h-24 object-cover rounded-lg border border-slate-200"
                  />
                  <button
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80 transition-colors"
                    data-testid={`observation-photo-remove-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <Input
                    value={photo.caption}
                    onChange={(e) => updateCaption(idx, e.target.value)}
                    placeholder="Caption"
                    className="mt-1 h-7 text-[11px] px-2"
                    data-testid={`observation-caption-input-${idx}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Add photo button (shown when under limit) */}
          {photos.length < MAX_PHOTOS && (
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={() => fileInputRef.current?.click()}
              data-testid="observation-capture-photo-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              {photos.length === 0 ? 'Capture Photo' : `Add Photo (${photos.length}/${MAX_PHOTOS})`}
            </Button>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={onCancel} className="flex-1" data-testid="observation-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1"
            data-testid="observation-save-btn"
          >
            Save Observation
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ─── Observation Card ─── */
function ObservationCard({ obs, onDelete, onPhotoTap, onUpdate, sessionId, isOnline, onReload }) {
  const severity = SEVERITIES.find(s => s.value === obs.severity) || SEVERITIES[0];
  const category = CATEGORIES.find(c => c.value === obs.category);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(obs.note_text || '');
  const [editCategory, setEditCategory] = useState(obs.category || 'general');
  const [editSeverity, setEditSeverity] = useState(obs.severity || 'info');
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(obs, { note_text: editText, category: editCategory, severity: editSeverity });
    }
    setEditing(false);
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const serverId = obs.server_id || obs.id;
    if (!isOnline || !serverId) {
      toast.error('Must be online to add photos to synced observations');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/count/observations/${serverId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo added');
      onReload();
    } catch (err) {
      toast.error('Photo upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/count/photos/${photoId}`);
      toast.info('Photo removed');
      onReload();
    } catch {
      toast.error('Failed to delete photo');
    }
  };

  const photos = obs.photos || [];

  return (
    <Card className="p-3" data-testid={`observation-card-${obs.local_id || obs.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            {category && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{category.label}</Badge>
            )}
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${severity.color}`}>
              {severity.label}
            </Badge>
            {obs.synced || obs._fromServer ? (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
                <Check className="w-3 h-3 mr-0.5" /> Synced
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                Local
              </Badge>
            )}
          </div>

          {editing ? (
            <div className="space-y-2 mt-1">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full text-sm p-2 border rounded-md min-h-[60px] resize-none"
                data-testid="observation-edit-text"
              />
              <div className="flex gap-2 flex-wrap">
                <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} className="text-xs border rounded px-2 py-1">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={editSeverity} onChange={(e) => setEditSeverity(e.target.value)} className="text-xs border rounded px-2 py-1">
                  {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} data-testid="observation-save-edit-btn">Save</Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditText(obs.note_text || ''); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-800 cursor-pointer hover:text-blue-700" onClick={() => setEditing(true)} title="Click to edit">{obs.note_text}</p>
          )}

          {(obs.product_name_snapshot || obs.location_name_snapshot) && (
            <p className="text-xs text-slate-400 mt-1">
              {obs.product_name_snapshot && <span>{obs.product_name_snapshot}</span>}
              {obs.product_name_snapshot && obs.location_name_snapshot && <span> &middot; </span>}
              {obs.location_name_snapshot && <span>{obs.location_name_snapshot}</span>}
            </p>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <div className="flex gap-1.5 mt-2 flex-wrap" data-testid={`observation-photos-${obs.local_id || obs.id}`}>
              {photos.map(p => (
                <div key={p.id || p.local_id} className="relative group">
                  <button
                    onClick={() => onPhotoTap(p.storage_url || p.preview)}
                    className="cursor-pointer"
                    data-testid={`observation-photo-thumb-${p.id || p.local_id}`}
                  >
                    <img
                      src={p.storage_url || p.preview}
                      alt={p.caption || 'Photo'}
                      className="w-20 h-20 object-cover rounded-lg border border-slate-200 group-hover:border-cyan-400 transition-colors"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                      <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                  {isOnline && p.id && (
                    <button
                      onClick={() => handleDeletePhoto(p.id)}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add photo button */}
          {photos.length < MAX_PHOTOS && (obs.synced || obs._fromServer) && isOnline && (
            <>
              <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleAddPhoto} />
              <Button
                variant="ghost"
                size="sm"
                className="mt-1.5 text-xs text-slate-500 h-7 px-2"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Camera className="w-3 h-3 mr-1" />}
                {uploading ? 'Uploading...' : 'Add Photo'}
              </Button>
            </>
          )}

          <p className="text-[10px] text-slate-300 mt-1">
            {new Date(obs.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="p-1 text-slate-300 hover:text-blue-500 transition-colors"
              title="Edit observation"
              data-testid={`observation-edit-${obs.local_id || obs.id}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-1 text-slate-300 hover:text-red-500 transition-colors"
            data-testid={`observation-delete-${obs.local_id || obs.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Card>
  );
}
